// Ниши › editor — one vertical, four tabs, one save. The draft is the whole editable row; save
// writes it back with `update … eq('key')` and shows the error if the database refuses it.

import { useState } from 'react';
import { useUpdateVertical, type VerticalPatch, type VerticalRow } from './useVerticals.js';
import { VerticalAuditTab } from './VerticalAuditTab.js';
import { VerticalBasicsTab } from './VerticalBasicsTab.js';
import { VerticalDiscoveryTab } from './VerticalDiscoveryTab.js';
import { VerticalTalkTab } from './VerticalTalkTab.js';

const TABS = [
  { key: 'basics', label: 'Основни' },
  { key: 'discovery', label: 'Откриване' },
  { key: 'audit', label: 'Одит' },
  { key: 'talk', label: 'Разговор' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function toPatch(row: VerticalRow): VerticalPatch {
  const { key: _key, origin: _origin, ...patch } = row;
  return patch;
}

export function VerticalEditor({
  vertical,
  onClose,
}: {
  vertical: VerticalRow;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<TabKey>('basics');
  const [draft, setDraft] = useState<VerticalPatch>(() => toPatch(vertical));
  const [dirty, setDirty] = useState(false);
  const save = useUpdateVertical();

  const set = <K extends keyof VerticalPatch>(field: K, value: VerticalPatch[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  return (
    <div className="adm__editor">
      <div className="adm__editor-head">
        <div>
          <h3 className="adm__h">
            {vertical.name_bg} <code className="adm__key">{vertical.key}</code>
          </h3>
          <span className="adm__sub">
            произход: {vertical.origin} · {draft.active ? 'активна' : 'неактивна'}
          </span>
        </div>
        <div className="adm__actions">
          <button
            type="button"
            className="adm__resolve-btn"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate({ key: vertical.key, patch: draft }, { onSuccess: () => setDirty(false) })
            }
          >
            {save.isPending ? 'Записва…' : 'Запази'}
          </button>
          <button type="button" className="adm__json-btn" onClick={onClose}>
            Затвори
          </button>
        </div>
      </div>
      {save.isError ? <p className="adm__err">{(save.error as Error).message}</p> : null}
      {save.isSuccess && !dirty ? <p className="adm__note">Записано.</p> : null}

      <nav className="adm__subnav">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`adm__tab ${tab === entry.key ? 'adm__tab--on' : ''}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {tab === 'basics' ? <VerticalBasicsTab draft={draft} set={set} /> : null}
      {tab === 'discovery' ? <VerticalDiscoveryTab draft={draft} set={set} /> : null}
      {tab === 'audit' ? (
        <VerticalAuditTab verticalKey={vertical.key} draft={draft} set={set} />
      ) : null}
      {tab === 'talk' ? <VerticalTalkTab draft={draft} set={set} /> : null}
    </div>
  );
}
